import {
  CustomGroup,
  Member,
  MemberAlias,
  MemberMap,
  User,
} from '@nextforce/api'
import {
  Icon,
  Input,
  ListItem,
  Switch,
  Text,
  makeStyles,
  useTheme,
} from '@rneui/themed'
import { t } from 'app/i18l'
import { MemberAliasItem } from 'chat/members/views/member_alias'
import { MemberSlugList } from 'chat/members/views/slug_list'
import { Image } from 'expo-image'
import React, { useCallback, useEffect, useState } from 'react'
import { View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { SafeAreaView } from 'react-native-safe-area-context'
import InfoPopup from 'shared/components/info_popup'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { pickImageFromLibrary } from 'shared/image_picker'
import { toggleItemInObjectMap } from 'shared/utils'

const ICON_SIZE = 48

interface Props {
  user: User | undefined
  members: MemberMap
  group: CustomGroup | undefined
  onGroupChanged(group: CustomGroup): void
  onMembersChanged(members: MemberMap): void
  selectedMemberAliases: MemberAlias[] | undefined
}

export function CreateGroupView({
  user,
  members,
  group,
  onGroupChanged,
  onMembersChanged,
  selectedMemberAliases,
}: Props) {
  const styles = useStyles()
  const { theme } = useTheme()
  const [icon, setIcon] = useState(group?.icon)
  const [name, setName] = useState(group?.name ?? '')
  const [description, setDescription] = useState(group?.description)
  const [crewReadOnly, setCrewReadOnly] = useState<boolean>(false)
  const [managerReadOnly, setManagerReadOnly] = useState<boolean>(false)
  const [repository, setRepository] = useState<boolean>(false)

  const canCreateRepository = user?.chat?.allowCreate.includes('repository')

  useEffect(() => {
    onGroupChanged({
      name,
      description,
      icon,
      crewReadOnly,
      managerReadOnly,
      repository,
    })
  }, [
    crewReadOnly,
    description,
    icon,
    managerReadOnly,
    name,
    onGroupChanged,
    repository,
  ])

  const onIconPress = useCallback(async () => {
    const pickImageResult = await pickImageFromLibrary()

    if (pickImageResult.status === 'success') {
      setIcon(pickImageResult.files[0])
    }
  }, [])

  const onMemberRemoved = useCallback(
    (member: Member) => {
      const updatedMembers = toggleItemInObjectMap(members, member.id, member)
      onMembersChanged(updatedMembers)
    },
    [members, onMembersChanged]
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <View style={styles.nameIconBox}>
          {!repository ? (
            <PressableOpacity style={styles.iconButton} onPress={onIconPress}>
              {icon ? (
                <Image style={styles.icon} source={{ uri: icon.uri }} />
              ) : (
                <View style={styles.icon}>
                  <Image
                    style={styles.defaultIconImage}
                    source={require('assets/chat/camera.png')}
                  />
                </View>
              )}
            </PressableOpacity>
          ) : (
            <Icon name="folder" type="material-design" style={styles.icon} />
          )}
          <Input
            style={styles.name}
            placeholder="Group name"
            value={name}
            onChangeText={setName}
            autoFocus
            placeholderTextColor={theme.colors?.grey4}
          />
        </View>
        <Input
          style={styles.description}
          placeholder="Description (optional)"
          value={description}
          onChangeText={setDescription}
          multiline
          placeholderTextColor={theme.colors?.grey4}
        />
        <Text style={styles.membersLabel}>{t('members').toUpperCase()}</Text>
        {selectedMemberAliases?.map(alias => (
          <MemberAliasItem
            key={alias}
            item={alias}
            selected={true}
            onToggle={() => null}
            containerStyle={styles.aliasRow}
          />
        ))}
        <MemberSlugList
          members={Object.values(members)}
          onMemberRemoved={onMemberRemoved}
        />
        {canCreateRepository ? (
          <ListItem containerStyle={styles.repositoryGroupItem}>
            <View style={styles.repositoryGroupLabelContainer}>
              <Text style={styles.repositoryGroupLabel}>
                {t('filesGroup').toUpperCase()}
              </Text>
              <InfoPopup message={t('filesGroupDescription')} />
            </View>
            <Switch value={repository} onValueChange={setRepository} />
          </ListItem>
        ) : null}

        {!repository ? (
          <>
            <Text style={styles.readOnlyLabel}>
              {t('readOnly').toUpperCase()}
            </Text>
            <ListItem containerStyle={styles.readOnlyTextContainer}>
              <ListItem.Content>
                <ListItem.Title style={styles.readOnlyText}>
                  {t('crew')}
                </ListItem.Title>
              </ListItem.Content>
              <Switch value={crewReadOnly} onValueChange={setCrewReadOnly} />
            </ListItem>
            <ListItem containerStyle={styles.readOnlyTextContainer}>
              <ListItem.Content>
                <ListItem.Title style={styles.readOnlyText}>
                  {t('manager')}
                </ListItem.Title>
              </ListItem.Content>
              <Switch
                value={managerReadOnly}
                onValueChange={setManagerReadOnly}
              />
            </ListItem>
          </>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    padding: 10,
    paddingBottom: 50,
  },
  nameIconBox: {
    height: ICON_SIZE,
    margin: 15,
    justifyContent: 'flex-start',
    flexDirection: 'row',
  },
  iconButton: {
    height: ICON_SIZE,
    width: ICON_SIZE,
  },
  defaultIconImage: {
    width: 24,
    height: 20,
  },
  icon: {
    justifyContent: 'center',
    alignItems: 'center',
    height: ICON_SIZE,
    width: ICON_SIZE,
    borderRadius: ICON_SIZE / 2,
    backgroundColor: theme.colors?.grey4,
  },
  name: {
    flex: 1,
    height: 40,
    marginStart: 10,
    marginTop: 5,
    fontSize: 17,
    color: theme.colors?.black,
  },
  description: {
    height: 80,
    marginHorizontal: 20,
    marginBottom: 20,
    fontSize: 14,
    textAlignVertical: 'top',
    color: theme.colors?.grey2,
  },
  membersLabel: {
    paddingHorizontal: 10,
    fontSize: 17,
    color: theme.colors?.grey1,
    marginBottom: 5,
  },
  readOnlyLabel: {
    paddingHorizontal: 10,
    marginTop: 20,
    fontSize: 17,
    color: theme.colors?.grey1,
  },
  repositoryGroupLabel: {
    paddingLeft: 10,
    fontSize: 17,
    color: theme.colors?.grey1,
  },
  readOnlyTextContainer: { paddingVertical: 5 },
  readOnlyText: {
    fontSize: 14,
    color: theme.colors?.grey2,
  },
  repositoryGroupItem: {
    paddingLeft: 0,
    paddingVertical: 0,
  },
  repositoryGroupLabelContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  aliasRow: {
    borderBottomWidth: 0,
    height: 35,
  },
}))
